import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, MessageCircle, AlertCircle, Clock, 
  CheckCircle2, X, Loader2, Send, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { showToast } from '../utils/toast';
import { formatDateEST, formatDateTimeEST } from '../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientSupport() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [creating, setCreating] = useState(false);
  const [replying, setReplying] = useState(false);

  // Create ticket form
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    priority: 'Medium'
  });

  // Reply form
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await fetch(`${API}/api/client/support/tickets`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      } else if (response.status === 401) {
        navigate('/client/login');
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      showToast({
        title: 'Error',
        message: 'Failed to load support tickets',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      showToast({
        title: 'Missing information',
        message: 'Please fill in all required fields',
        type: 'error'
      });
      return;
    }

    setCreating(true);

    try {
      const response = await fetch(`${API}/api/client/support/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newTicket)
      });

      if (response.ok) {
        showToast({
          title: 'Success',
          message: 'Support ticket created successfully',
          type: 'success'
        });
        setShowCreateModal(false);
        setNewTicket({ subject: '', description: '', priority: 'Medium' });
        await fetchTickets();
      } else {
        throw new Error('Failed to create ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      showToast({
        title: 'Error',
        message: 'Failed to create support ticket',
        type: 'error'
      });
    } finally {
      setCreating(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await fetch(`${API}/api/client/support/ticket/${ticketId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedTicket(data.ticket);
        setShowTicketDetail(true);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      showToast({
        title: 'Error',
        message: 'Failed to load ticket details',
        type: 'error'
      });
    }
  };

  const handleAddReply = async () => {
    if (!replyMessage.trim()) {
      showToast({
        title: 'Empty message',
        message: 'Please enter a message',
        type: 'error'
      });
      return;
    }

    setReplying(true);

    try {
      const response = await fetch(`${API}/api/client/support/reply/${selectedTicket.ticket_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: replyMessage })
      });

      if (response.ok) {
        setReplyMessage('');
        await fetchTicketDetails(selectedTicket.ticket_id);
        showToast({
          title: 'Reply sent',
          message: 'Your reply has been added',
          type: 'success'
        });
      } else {
        throw new Error('Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      showToast({
        title: 'Error',
        message: 'Failed to send reply',
        type: 'error'
      });
    } finally {
      setReplying(false);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;

    try {
      const response = await fetch(`${API}/api/client/support/close/${ticketId}`, {
        method: 'PATCH',
        credentials: 'include'
      });

      if (response.ok) {
        showToast({
          title: 'Ticket closed',
          message: 'Support ticket has been closed',
          type: 'success'
        });
        setShowTicketDetail(false);
        await fetchTickets();
      }
    } catch (error) {
      console.error('Error closing ticket:', error);
      showToast({
        title: 'Error',
        message: 'Failed to close ticket',
        type: 'error'
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open':
        return <AlertCircle className="w-5 h-5 text-yellow-400" />;
      case 'In Progress':
        return <Clock className="w-5 h-5 text-blue-400" />;
      case 'Resolved':
        return <CheckCircle2 className="w-5 h-5 text-green-400" />;
      case 'Closed':
        return <CheckCircle2 className="w-5 h-5 text-gray-400" />;
      default:
        return <MessageCircle className="w-5 h-5 text-blue-400" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'High':
        return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'Medium':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'Low':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      default:
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  // Ticket Detail View
  if (showTicketDetail && selectedTicket) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <Button
            onClick={() => setShowTicketDetail(false)}
            variant="ghost"
            className="mb-4 text-blue-300 hover:text-blue-200 hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to All Tickets
          </Button>

          {/* Ticket Header */}
          <div className="backdrop-blur-xl bg-white/10 rounded-xl border border-white/20 p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white mb-2">
                  {selectedTicket.subject}
                </h1>
                <p className="text-blue-200">
                  {selectedTicket.description}
                </p>
              </div>
              {getStatusIcon(selectedTicket.status)}
            </div>

            <div className="flex flex-wrap gap-3 text-sm">
              <span className={`px-3 py-1 rounded-full border ${getPriorityColor(selectedTicket.priority)}`}>
                {selectedTicket.priority}
              </span>
              <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
                {selectedTicket.status}
              </span>
              <span className="text-blue-200">
                Created: {formatDateEST(selectedTicket.created_at)}
              </span>
            </div>

            {selectedTicket.status !== 'Closed' && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <Button
                  onClick={() => handleCloseTicket(selectedTicket.ticket_id)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-red-500/20 hover:border-red-500/30"
                >
                  Close Ticket
                </Button>
              </div>
            )}
          </div>

          {/* Conversation Thread */}
          <div className="backdrop-blur-xl bg-white/10 rounded-xl border border-white/20 p-6 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4">Conversation</h2>
            
            <div className="space-y-4">
              {selectedTicket.replies && selectedTicket.replies.length > 0 ? (
                selectedTicket.replies.map((reply) => (
                  <div
                    key={reply.reply_id}
                    className={`p-4 rounded-lg ${
                      reply.sender_type === 'client'
                        ? 'bg-blue-500/20 border border-blue-500/30 ml-8'
                        : 'bg-purple-500/20 border border-purple-500/30 mr-8'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-white">
                        {reply.sender_name}
                      </span>
                      <span className="text-xs text-blue-200">
                        {formatDateTimeEST(reply.timestamp)}
                      </span>
                    </div>
                    <p className="text-blue-100">{reply.message}</p>
                  </div>
                ))
              ) : (
                <p className="text-blue-300 text-center py-8">
                  No replies yet. Start the conversation below.
                </p>
              )}
            </div>
          </div>

          {/* Reply Form */}
          {selectedTicket.status !== 'Closed' && (
            <div className="backdrop-blur-xl bg-white/10 rounded-xl border border-white/20 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Add Reply</h3>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type your message here..."
                rows={4}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500 mb-4"
              />
              <Button
                onClick={handleAddReply}
                disabled={replying}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
              >
                {replying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Reply
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Tickets List View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/client-dashboard')}
            variant="ghost"
            className="mb-4 text-blue-300 hover:text-blue-200 hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                Support Tickets
              </h1>
              <p className="text-blue-200">
                Get help with your projects and services
              </p>
            </div>

            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Ticket
            </Button>
          </div>
        </div>

        {/* Tickets List */}
        {tickets.length > 0 ? (
          <div className="grid gap-4">
            {tickets.map((ticket) => (
              <div
                key={ticket.ticket_id}
                onClick={() => fetchTicketDetails(ticket.ticket_id)}
                className="backdrop-blur-xl bg-white/10 rounded-xl border border-white/20 p-6 hover:bg-white/15 cursor-pointer transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getStatusIcon(ticket.status)}
                      <h3 className="text-lg font-semibold text-white group-hover:text-blue-300 transition-colors">
                        {ticket.subject}
                      </h3>
                    </div>
                    <p className="text-blue-200 mb-3 line-clamp-2">
                      {ticket.description}
                    </p>
                    <div className="flex flex-wrap gap-2 text-sm">
                      <span className={`px-2 py-1 rounded-full border text-xs ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                      <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 text-xs">
                        {ticket.status}
                      </span>
                      {ticket.replies && ticket.replies.length > 0 && (
                        <span className="px-2 py-1 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 text-xs">
                          {ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-xs text-blue-200 mb-2">
                      {formatDateEST(ticket.created_at)}
                    </p>
                    <ArrowRight className="w-5 h-5 text-blue-300 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="backdrop-blur-xl bg-white/10 rounded-xl border border-white/20 p-12 text-center">
            <MessageCircle className="w-16 h-16 text-blue-300 mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-semibold text-white mb-2">
              No support tickets yet
            </h3>
            <p className="text-blue-200 mb-6">
              Create your first ticket to get help from our team
            </p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Ticket
            </Button>
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-2xl border border-white/20 p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Create Support Ticket</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-blue-300 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of the issue"
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Priority
                </label>
                <select
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Description *
                </label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  placeholder="Provide detailed information about your issue..."
                  rows={5}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Buttons */}
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={() => setShowCreateModal(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  disabled={creating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateTicket}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Ticket
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
